// How trade results are shown across the journal: dollars, % of the account, points,
// ticks, R (multiples of the risk, rMultiple.js), or Privacy (every dollar amount hidden). Picked at the top of the Dashboard or the
// Trades page and remembered in this browser. Privacy only applies inside the journal (the
// app sets it active), so the public pages and tools always show real numbers.
import { useSyncExternalStore } from 'react';
import { contractFor, pointValue } from './tools/contracts';
import { setPrivacy } from './privacy';
import { rOf } from './rMultiple';

const KEY = 'mm-display';
export const DISPLAY_MODES = [
  ['pnl', 'Dollars'],
  ['pct', '% of account'],
  ['points', 'Points'],
  ['ticks', 'Ticks'],
  ['r', 'R (risk multiples)'],
  ['privacy', 'Privacy (hide amounts)'],
];

function read() {
  try {
    const saved = localStorage.getItem(KEY);
    return DISPLAY_MODES.some(([k]) => k === saved) ? saved : 'pnl';
  } catch {
    return 'pnl';
  }
}

let mode = read();
let active = false;
const listeners = new Set();

export function displayMode() {
  return mode;
}

const sync = () => setPrivacy(active && mode === 'privacy');

export function setDisplayMode(next) {
  mode = next;
  sync();
  try { localStorage.setItem(KEY, next); } catch { /* not remembered */ }
  listeners.forEach((fn) => fn());
}

// The journal turns this on while it's open, and off when you leave it.
export function setDisplayActive(on) {
  active = on;
  sync();
}

export function useDisplayMode() {
  return useSyncExternalStore((fn) => { listeners.add(fn); return () => listeners.delete(fn); }, () => mode);
}

// The price move of each futures trade, per contract (not times the size), in points and ticks.
export function tradeMoves(trades) {
  const out = { points: 0, futures: 0, ticks: 0, ticked: 0, r: 0, rCounted: 0 };
  for (const t of trades) {
    const r = rOf(t);
    if (r !== null) { out.r += r; out.rCounted += 1; }
    if (!pointValue(t.symbol) || !Number.isFinite(t.entryPrice) || !Number.isFinite(t.exitPrice)) continue;
    const move = (t.exitPrice - t.entryPrice) * (t.side === 'Short' ? -1 : 1);
    out.points += move;
    out.futures += 1;
    const contract = contractFor(t.symbol);
    if (contract) {
      out.ticks += Math.round(move / contract.tick);
      out.ticked += 1;
    }
  }
  return out;
}

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');

// A result in the chosen display: `net` is the dollars, `moves` the tradeMoves of the same trades,
// `size` the account size for %. `average` divides by `count` (e.g. average win). Without what a
// mode needs (no account size, no futures trades) it falls back to dollars.
export function showResult({ net, moves, size, count = 1, average = false, money, t, mode: m = mode }) {
  const per = (v, n) => (average ? v / Math.max(1, n) : v);
  if (m === 'pct' && size) {
    const pct = (per(net, count) / size) * 100;
    return { text: `${pct > 0 ? '+' : ''}${pct.toFixed(Math.abs(pct) >= 10 ? 1 : 2)}%`, tone: tone(net) };
  }
  if ((m === 'points' || m === 'ticks') && moves && !moves.futures && !net) return { text: `0 ${t(m === 'points' ? 'pts' : 'ticks')}`, tone: '' };
  if (m === 'points' && moves && moves.futures) {
    const p = Math.round(per(moves.points, moves.futures) * 100) / 100;
    return { text: `${p > 0 ? '+' : ''}${Number.isInteger(p) ? p : p.toFixed(2)} ${t('pts')}`, tone: tone(p) };
  }
  if (m === 'r' && moves && moves.rCounted) {
    const r = Math.round(per(moves.r, moves.rCounted) * 100) / 100;
    return { text: `${r > 0 ? '+' : ''}${r.toFixed(2)}R`, tone: tone(r) };
  }
  if (m === 'ticks' && moves && moves.ticked) {
    const k = Math.round(per(moves.ticks, moves.ticked) * 10) / 10;
    return { text: `${k > 0 ? '+' : ''}${k} ${t('ticks')}`, tone: tone(k) };
  }
  return { text: money(per(net, count)), tone: tone(net) };
}
